// Lives (hearts) system — pure utility, no framework deps

import { RANKS, getRankForLevel } from './ranks.js';
import { getItem, setItem } from './storage.js';

const STORAGE_KEY = 'minesweeper_lives';
const BASE_MAX_HEARTS = 3;
const TOTAL_MAX_HEARTS = 6;
export const REFILL_MS = 20 * 60 * 1000; // 20 minutes

/**
 * Current max hearts based on rank (counts bonus slots from all ranks up to current).
 */
export function getMaxHearts()
{
    const cleared = parseInt( getItem( 'minesweeper_cleared' ) || '0', 10 );
    const { rankIndex } = getRankForLevel( cleared );
    let max = BASE_MAX_HEARTS;
    for ( let i = 0; i <= rankIndex; i++ )
    {
        if ( RANKS[ i ].bonus ) max++;
    }
    return Math.min( max, TOTAL_MAX_HEARTS );
}

function load()
{
    const max = getMaxHearts();
    try
    {
        const raw = getItem( STORAGE_KEY );
        if ( raw )
        {
            const parsed = JSON.parse( raw );
            if ( typeof parsed.hearts === 'number' && parsed.hearts >= 0 && parsed.hearts <= max )
            {
                return { hearts: parsed.hearts, depletedAt: parsed.depletedAt ?? null };
            }
        }
    }
    catch {}
    return { hearts: max, depletedAt: null };
}

function save( state )
{
    setItem( STORAGE_KEY, JSON.stringify( state ) );
}

/**
 * Get current lives state, applying any offline refills.
 */
export function getLivesState()
{
    const max = getMaxHearts();
    const state = load();

    if ( state.hearts < max && state.depletedAt !== null )
    {
        const elapsed = Date.now() - state.depletedAt;
        const refills = Math.floor( elapsed / REFILL_MS );
        if ( refills > 0 )
        {
            state.hearts = Math.min( max, state.hearts + refills );
            if ( state.hearts >= max )
            {
                state.depletedAt = null;
            }
            else
            {
                state.depletedAt = state.depletedAt + refills * REFILL_MS;
            }
            save( state );
        }
    }

    return state;
}

/**
 * Decrement one heart and persist. Returns updated state.
 */
export function loseHeart()
{
    const state = getLivesState();
    if ( state.hearts <= 0 ) return state;

    if ( state.hearts === getMaxHearts() )
    {
        state.depletedAt = Date.now();
    }
    state.hearts--;
    save( state );
    return state;
}

/**
 * Refill hearts to max and persist. Returns updated state.
 */
export function refillAllHearts()
{
    const max = getMaxHearts();
    const state = { hearts: max, depletedAt: null };
    save( state );
    return state;
}

/**
 * Add one heart (capped at max) and persist. Returns updated state.
 */
export function gainHeart()
{
    const max = getMaxHearts();
    const state = getLivesState();
    if ( state.hearts >= max ) return state;
    state.hearts++;
    if ( state.hearts >= max )
    {
        state.depletedAt = null;
    }
    save( state );
    return state;
}

/**
 * Milliseconds until next heart refill (0 if full).
 */
export function getNextRefillMs()
{
    const state = getLivesState();
    if ( state.hearts >= getMaxHearts() || state.depletedAt === null ) return 0;
    const elapsed = Date.now() - state.depletedAt;
    const nextAt = ( Math.floor( elapsed / REFILL_MS ) + 1 ) * REFILL_MS;
    return Math.max( 0, nextAt - elapsed );
}

/**
 * Format milliseconds as "MM:SS".
 */
export function formatRefillTimer( ms )
{
    const totalSec = Math.max( 0, Math.ceil( ms / 1000 ) );
    const m = Math.floor( totalSec / 60 );
    const s = totalSec % 60;
    return String( m ).padStart( 2, '0' ) + ':' + String( s ).padStart( 2, '0' );
}

/**
 * Render hearts HTML string: filled ♥ + empty ♡.
 */
export function renderHeartsHtml( hearts )
{
    const max = getMaxHearts();
    return '\u2665'.repeat( hearts ) + '\u2661'.repeat( max - hearts );
}
